/**
 * Access request approval page.
 *
 * The profile owner clicks "Approve Access" in their notification email
 * and lands here. Token from the email is the proof of identity.
 * Public route — no authentication required.
 */

import { useApproveAccessRequestMutation } from "@/lib/queries/projects";
import {
  Button,
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

const searchSchema = z.object({
  token: z.string().min(1, "Token is required"),
});

export const Route = createFileRoute("/access-requests/approve")({
  validateSearch: searchSchema,
  component: ApproveAccessRequestPage,
});

export function ApproveAccessRequestPage() {
  const { token } = Route.useSearch();
  const mutation = useApproveAccessRequestMutation();

  useEffect(() => {
    mutation.mutate({ token });
    // Run once on mount
  }, [token]);

  if (mutation.isPending || mutation.isIdle) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
            <CardTitle>Approving access...</CardTitle>
            <CardDescription>Please wait a moment.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (mutation.isError) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Approval failed</CardTitle>
            <CardDescription>
              {(mutation.error as Error)?.message ??
                "Something went wrong. Please try again."}
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const data = mutation.data;

  if (data && "status" in data && data.status === "already_approved") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Already Approved</CardTitle>
            <CardDescription>
              This access request was already approved. An invitation was sent.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (data && "status" in data && data.status === "rejected") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <AlertCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Request Already Rejected</CardTitle>
            <CardDescription>
              This access request was previously rejected.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (data && "status" in data && data.status === "expired") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle>Link Expired</CardTitle>
            <CardDescription>
              This approval link has expired. Ask the requestor to submit a new
              access request.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Success
  const requestorEmail =
    data && "requestorEmail" in data ? data.requestorEmail : null;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle>Access Approved</CardTitle>
          <CardDescription>
            {requestorEmail ? (
              <>
                An invitation has been sent to{" "}
                <span className="font-medium">{requestorEmail}</span>.
              </>
            ) : (
              "An invitation has been sent to the requestor."
            )}
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-center">
          <Link to="/">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
